'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { withBase } from '@/lib/base-path'
import { pushEvent } from '@/lib/tracking'

interface DownloadPdfButtonProps {
  sessionId: string;
  token?: string;
  label?: string;
  variant?: React.ComponentProps<typeof Button>['variant'];
  size?: React.ComponentProps<typeof Button>['size'];
  className?: string;
}

export default function DownloadPdfButton({
  sessionId,
  token,
  label = 'Download PDF',
  variant = 'primary',
  size = 'sm',
  className,
}: DownloadPdfButtonProps) {
  const [pending, setPending] = useState(false);

  async function handleClick() {
    setPending(true);
    try {
      const url = new URL(
        withBase(`/api/interview/session/${encodeURIComponent(sessionId)}/output/pdf`),
        window.location.origin
      );
      if (token) url.searchParams.set('session_token', token);

      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Download mislukt (${response.status})`);
      }

      pushEvent('ai_discovery_pdf_download');
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const match = /filename="?([^"]+)"?/.exec(contentDisposition);
      const filename = match?.[1] || 'spaik-rapport.pdf';

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('[download-pdf]', err);
      window.alert(
        err instanceof Error ? err.message : 'Er ging iets mis bij het downloaden van de PDF.'
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      loading={pending}
      disabled={pending}
      onClick={handleClick}
    >
      {label}
    </Button>
  );
}
